import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar, CalendarPermissionScope } from '@ebarooni/capacitor-calendar';
import { pushIOSCalendarEvents } from './reservationService';

export interface CalendarEvent {
  id: string;
  start: string;
  end: string;
  summary: string;
}

export interface SyncResult {
  skipped?: boolean;
  granted?: boolean;
  count?: number;
  modified?: boolean;
}

// How far ahead to import busy events (mirrors the server's future-only filtering).
const SYNC_MONTHS_AHEAD = 6;

// The native EventKit plugin only runs inside the installed iOS app, never on web/PWA.
export const isNativeIOS = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

// Reading events on iOS 17+ requires FULL calendar access (write-only access can't read).
export const ensureCalendarAccess = async (): Promise<boolean> => {
  const { result } = await CapacitorCalendar.requestFullCalendarAccess();
  return result === 'granted';
};

// Non-prompting check — used to decide whether a silent background sync may run.
export const hasCalendarAccess = async (): Promise<boolean> => {
  try {
    const { result } = await CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.READ_CALENDAR });
    return result === 'granted';
  } catch {
    return false;
  }
};

// Read the device calendar in [from, to] and normalize to the server's { id, start, end, summary } shape.
export const readDeviceEvents = async (from: Date, to: Date): Promise<CalendarEvent[]> => {
  const { result } = await CapacitorCalendar.listEventsInRange({
    from: from.getTime(),
    to: to.getTime(),
  });
  return (result || [])
    .filter((e) => e && e.id && e.startDate && e.endDate)
    .map((e) => ({
      id: e.id,
      start: new Date(e.startDate).toISOString(),
      end: new Date(e.endDate).toISOString(),
      summary: e.title || 'Busy',
    }));
};

// End-to-end: ensure permission, read the device calendar, push to the server (which blocks the slots).
// Safe to call anywhere — it no-ops on web and returns a small status object.
export const syncDeviceCalendar = async (stylistId: string): Promise<SyncResult> => {
  if (!isNativeIOS() || !stylistId) return { skipped: true };
  const granted = await ensureCalendarAccess();
  if (!granted) return { granted: false };

  const from = new Date();
  const to = new Date();
  to.setMonth(to.getMonth() + SYNC_MONTHS_AHEAD);

  const events = await readDeviceEvents(from, to);
  const res = await pushIOSCalendarEvents(stylistId, events);
  return { granted: true, count: events.length, ...(res as SyncResult) };
};

// Silent variant for auto-sync (page mount / app resume): only runs if access is already granted,
// so it never surprises the stylist with a permission prompt.
export const syncIfAuthorized = async (stylistId: string): Promise<SyncResult> => {
  if (!isNativeIOS() || !stylistId) return { skipped: true };
  if (!(await hasCalendarAccess())) return { skipped: true, granted: false };
  const from = new Date();
  const to = new Date();
  to.setMonth(to.getMonth() + SYNC_MONTHS_AHEAD);
  const events = await readDeviceEvents(from, to);
  const res = await pushIOSCalendarEvents(stylistId, events);
  return { granted: true, count: events.length, ...(res as SyncResult) };
};
